import {
    AfterViewChecked,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Inject,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewContainerRef
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { MonitoringService, PrefTypeName } from '@bilancio/api';
import { AppShellComponent, ModalService } from '@bilancio/ui';
import { setAmChartsLicense } from '@vapor/angular-charts';
import { EmbedNavigationService } from '@vapor/angular-sdk';
import { HelpCenterService } from '@vapor/angular-sdk-ui';
import { devextremeConfig } from '@vapor/angular-ui-extra/config';
import { Nullable } from '@vapor/angular-utils';
import { licenseKey } from 'devextreme-license';
import { locale } from 'devextreme/localization';
import { BehaviorSubject, Observable, Subject, combineLatest, of } from 'rxjs';
import {
    catchError,
    filter,
    map,
    switchMap,
    take,
    takeUntil,
    tap
} from 'rxjs/operators';
import { AppSandbox } from './app.sandbox';
import { FooterDrawerService } from './components/footer-drawer/footer-drawer.service';
import { TourTemplateService } from './components/tour-template/tour-template.service';
import {
    AMCHART_LIC_CONFIG,
    AmchartsLicConfig
} from './core/tokens/amcharts.token';
import { TSDESK_CONFIG, TsDeskConfig } from './core/tokens/ts-desk.token';
import {
    TSINTRO_VIDEO_LINK,
    TsIntroVideoConfig
} from './core/tokens/tsintro-video.token';
import { TSLAND_LINK, TsLinkLandConfig } from './core/tokens/tslinkland.token';
import { InterframeGlobalService } from './interframe-global/interframe-global.service';
import { TsstudioIntegrazioneService } from './services/tsstudio-integrazione/tsstudio-integrazione.service';

export type TsDeskUser = {
    email: string;
    first_name: string;
    last_name: string;
    user_hash: string;
    registered_at: number;
    groups: Array<string>;
    traits: object;
};

/**
 * Componente principale  del programma
 *
 * @export
 * @class AppComponent
 * @implements {OnInit}
 */
@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    providers: [AppSandbox],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy, AfterViewChecked {
    /** Grid columns value */
    containerGridColumns = '64px auto';
    /** Route data */
    routeData$: Observable<{
        layout?: {
            showHeader?: boolean;
            showSidebar?: boolean;
        };
    }>;
    /** Whether menu labels should not visible */
    showMenuLabel = false;

    /** Username to show in user context menu */
    username$ = this.sandbox.username$;

    isAdmin$ = this.sandbox.isAdmin$;
    isViaLiberaDeposit$ = this.sandbox.isViaLiberaDeposit$;

    set isNoteRilascioVisible(isNoteRilascioVisible: boolean) {
        this._isNoteRilascioVisible = isNoteRilascioVisible;
    }

    get isNoteRilascioVisible(): boolean {
        return this._isNoteRilascioVisible;
    }

    isLastVersionReaded$ = this.sandbox.isLastVersionReaded$;
    allTrue$ = new BehaviorSubject<boolean>(true);
    temp$ = new BehaviorSubject<boolean>(true);

    /**
     * Div principale del programma
     *
     * @memberof AppComponent
     */
    @ViewChild('mainDiv', { read: ViewContainerRef, static: false })
    mainDivContainer;

    @ViewChild('appShell')
    appShell: AppShellComponent;

    destroyedTour$ = new Subject<boolean>();

    /** descrizioni per il tour */
    readonly descrCli =
        'Hai accesso a tutte le informazioni generali e di dettaglio dei tuoi clienti';
    readonly descrDashboard =
        'Trovi le informazioni principali relative al tuo Studio';
    readonly descrFascicoli =
        'Puoi lavorare su tutti i fascicoli che hai creato per ogni cliente.';
    readonly descrDeposito =
        'Troverai tutti i fascicoli depositati. Questa sezione non è ancora dispoibile.';
    readonly descrDocumenti =
        'Potrai consultare tutti i documenti necessari per redigere un bilancio. Questa sezione non è ancora dispoibile.';
    readonly descrStudio =
        'Se lavori in più Studi, potrai scegliere lo Studio per cui vuoi operare.';

    showUserMenu$ = new BehaviorSubject<boolean>(false);
    showExitIntegration$ = new BehaviorSubject<boolean>(false);
    isEmbedded$ = this.sandbox.isEmbedded$;
    isNomeCmd$ = this.sandbox.isNomeCmd$;
    isLybera$ = this.sandbox.isLybera$;
    isIntegration = false;
    tsIntroVideoUrl$ = new BehaviorSubject<string>('');
    tsIntroLinkLand$ = new BehaviorSubject<string>('');

    public user$: Observable<Partial<Nullable<TsDeskUser>>>;

    private _showUserMenu = false;

    private _isNoteRilascioVisible: boolean;
    private readonly destroy$ = new Subject<void>();
    private isEmbedded = false;
    public readonly elevioError$ = new Subject<boolean>();

    public readonly elevioAccountId = this.tsDeskConf.accountId;

    // *********************************************************************
    // *** Costruttore
    // *********************************************************************
    @ViewChild('video') video: ElementRef;
    /**
     * Inizializza la classe
     *
     * @param resolver - Resolver per la crazione dinamica dei componenti
     * @memberof AppComponent
     */
    constructor(
        readonly footerDrawerService: FooterDrawerService,
        readonly modalService: ModalService,
        readonly tour: TourTemplateService,
        private readonly activatedRoute: ActivatedRoute,
        private readonly router: Router,
        protected readonly sandbox: AppSandbox,
        private readonly changeDetectorRef: ChangeDetectorRef,
        readonly myMonitoringService: MonitoringService,
        @Inject(TSDESK_CONFIG) private readonly tsDeskConf: TsDeskConfig,
        @Inject(TSLAND_LINK) tsLinkLand: TsLinkLandConfig,
        @Inject(AMCHART_LIC_CONFIG)
        private readonly amchartsLic: AmchartsLicConfig,
        @Inject(TSINTRO_VIDEO_LINK) tsIntroVideoLink: TsIntroVideoConfig,
        private readonly srvTsStudioInt: TsstudioIntegrazioneService,
        private readonly embedNavigationService: EmbedNavigationService,
        private readonly interframeGlobalService: InterframeGlobalService,
        private readonly helpCenterService: HelpCenterService
    ) {
        devextremeConfig({
            locale: navigator.language,
            defaultCurrency: 'EUR',
            licenseKey: licenseKey
        });
        locale(navigator.language);
        this.tsIntroVideoUrl$.next(tsIntroVideoLink.tsIntroVideoLink);
        this.tsIntroLinkLand$.next(tsLinkLand.tslinkLand);
        combineLatest([
            this.sandbox.getCurrentOffice$,
            this.sandbox.isIntegration$
        ])
            .pipe(takeUntil(this.destroy$), takeUntil(this.elevioError$))
            .subscribe({
                next: ([office, isIntegration]) => {
                    this.isIntegration = isIntegration;
                    if (!isIntegration && office) {
                        this.user$ = this.helpCenterService
                            .user$(office.pivaCode ?? office.fiscalCode, {
                                ...this.tsDeskConf.standalone
                            })
                            .pipe(
                                catchError((e) => {
                                    this.elevioError$.next(true);
                                    console.log('elevio catch error', e);
                                    this.elevioError$.complete();
                                    return of(null);
                                })
                            );
                    }
                },
                error: (e) => {
                    this.elevioError$.next(true);
                    console.log('elevio error', e);
                    this.elevioError$.complete();
                }
            });
    }
    ngOnDestroy(): void {
        if (this.isEmbedded) {
            this.sandbox.logout();
        }
        this.sandbox.cleanState();
        this.destroy$.next();
        this.destroy$.complete();
    }

    // *********************************************************************
    // *** Eventi componente
    // *********************************************************************

    ngAfterViewChecked(): void {
        this.changeDetectorRef.detectChanges();
        // console.log('ECCOMI'); //@fixme can improve performance ???
    }

    /**
     * Evento di inizializzazione del componente
     *
     * @memberof AppComponent
     */
    ngOnInit() {
        setAmChartsLicense(this.amchartsLic.licence);
        this.isEmbedded$
            .pipe(takeUntil(this.destroy$))
            .subscribe((isEmbedded) => {
                this.isEmbedded = isEmbedded;
            });
        this.embedNavigationService.tenantId$
            .pipe(takeUntil(this.destroy$))
            .subscribe((el) => {
                this.sandbox.changeOffice(el);
            });
        this.sandbox.showTour$
            .pipe(takeUntil(this.destroy$))
            .subscribe((show) => {
                if (show) {
                    setTimeout(() => {
                        this.startTour();
                    }, 1000);
                    const prefsToSave = [
                        {
                            idCustomer: 0,
                            idDossier: 0,
                            nameSetting: PrefTypeName.viewTourLogin,
                            valueSetting: !show
                        }
                    ];
                    this.sandbox.updatePrefs(prefsToSave);
                }
            });

        this.createRouteData();

        this.isNoteRilascioVisible = false;
        this.sandbox.readFeVersion();
        this.sandbox.licId$.pipe(takeUntil(this.destroy$)).subscribe((el) => {
            if (el) {
                this.sandbox.asyncConn();
            }
        });
    }

    balloonShow(e, text, position?) {
        this.sandbox.balloonShow(e, text, position);
    }

    balloonHidden() {
        this.sandbox.balloonHidden();
    }

    /** Menu toggle to show labels */
    toggleMenuExplosion(): void {
        const explodedMenu = '192px auto';
        const closedMenu = '64px auto';

        this.showMenuLabel = !this.showMenuLabel;

        this.containerGridColumns = this.showMenuLabel
            ? explodedMenu
            : closedMenu;
    }

    public callBackExit(e: boolean): void {
        this.showExitIntegration$.next(false);

        if (!e) {
            return;
        }

        this.sandbox.isLybera$.pipe(take(1)).subscribe((isLybera) => {
            if (isLybera) {
                this.interframeGlobalService.sendMessageToParent({
                    Command: 'interframe-vl-logout'
                });
            } else {
                this.srvTsStudioInt.exit();
            }
        });
    }

    public callBackExitIntro() {
        this.sandbox.setNomeCmd();
        this.video.nativeElement.pause();
        this.video.nativeElement.src = '';
        this.video.nativeElement.load();
    }

    public showUserMenu() {
        this._showUserMenu = !this._showUserMenu;
        this.showUserMenu$.next(this._showUserMenu);
    }

    /** Create route data subscription */
    createRouteData(): void {
        this.routeData$ = this.router.events.pipe(
            filter((event) => event instanceof NavigationEnd),
            tap(() => this.balloonHidden()),
            map(() => this.activatedRoute),
            map((route) => route.firstChild),
            switchMap((route) => route.data)
        );
    }

    startTour() {
        const newUrlArray = this.router.url.split('/');
        let tour = [];
        if (
            newUrlArray.length === 3 &&
            newUrlArray[1] === 'user' &&
            newUrlArray[2] === 'dashboard'
        ) {
            tour = [
                'manage1',
                'manage2',
                'manage3',
                'manage4',
                'manage5',
                'manage6',
                'manage7',
                'manage8',
                'manage9',
                'manage10',
                'manage11',
                'manage12',
                'manage13'
            ];
        }
        if (tour.length > 0) {
            this.tour
                .startTour(tour)
                .pipe(takeUntil(this.destroyedTour$))
                .subscribe({
                    next: (step) => {
                        if (step.name === 'manage2') {
                            this.appShell.instanceScrollable.scrollBy({
                                top: -15000
                            });
                        }
                        if (step.name === 'manage10') {
                            this.appShell.instanceScrollable.scrollBy({
                                top: 15000
                            });
                        }
                    },
                    error: () => {
                        this.destroyedTour$.next(true);
                        this.destroyedTour$.complete();
                    },
                    complete: () => {
                        this.appShell.instanceScrollable.scrollBy({
                            top: -15000
                        });
                        this.destroyedTour$.next(true);
                        this.destroyedTour$.complete();
                    }
                });
        }
    }

    openNoteDiRilascio() {
        this.isNoteRilascioVisible = !this.isNoteRilascioVisible;
    }

    onOpenReleaseNote(): void {
        this.sandbox.readReleaseNote();
    }
}
